import {Ability, Size} from '../../../../generated/types';
import {abilityModifier, proficiencyBonusForLevel, SKILL_ABILITY} from './abilities';
import {calculateAbilityScores} from './ability-scores';
import type {Catalog, ClassData} from './catalog';
import {characterLevel, CharacterState, HitPointsState} from './character-state';
import {ChoiceScope} from './choice-scope';
import {
	ArmorClassBonus,
	CharacterAction,
	CharacterEffects,
	CharacterResource,
	CharacterSpell,
	collectEffects,
	CombatNote,
	Sourced,
	SpellcastingSource,
} from './collect-effects';
import {
	allFeatureInstances,
	CharacterFeatures,
	ChoiceInstance,
	FeatureInstance,
	FeatureOwner,
	flattenChoices,
} from './feature-tree';
import type {FormulaContext} from './formula';
import {collectPropsSources, PropsSource} from './props-sources';
import {calculateSpellSlots, findFullCasterTable, maxSpellLevel, SpellSlot} from './spell-slots';
import type {ExtraSpeed, FormulaValue, Recharge, RollBonus, Sense, SpellTable} from './types';
import {Attack, unarmedStrike, weaponAttack} from './attacks';
import {hasZeroSpeed, RollAdvice, rollAdvice, RollContext} from './conditions';
import {
	equipmentWeight,
	EquippedItem,
	isAmmunition,
	isBodyArmor,
	isRangedWeapon,
	isShield,
	isWeapon,
	ItemRecord,
	stackEquipment,
} from './equipment';
import {buildEquipment, itemChoiceValues, itemPropsSources} from './item-effects';
import {activeSpellChoiceValues, activeSpellPropsSources} from './active-spells';
import {AbilityOverflow, abilityOverflows, withAbilityMinimums} from './ability-scores';
import {CarryingCapacity, carryingCapacity} from './carrying';
import {abilityLabel} from './labels';

export type ProficiencyLevel = 'none' | 'proficient' | 'expertise';

export type Speeds = {walk: number} & Partial<Record<ExtraSpeed, number>>;

export type CharacterClass = {
	classData: ClassData;
	level: number;
	subClass?: {id: string; title: string};
	isStartingClass: boolean;
};

export type HitDice = {
	die: number;
	count: number;
	spent: number;
};

export type LimitedUse = {
	key: string;
	title: string;
	max: number;
	spent: number;
	recharge?: Recharge;
	regainsOnShortRest?: number;
	/** сколько возвращается на рассвете: `1d6+1` */
	regain?: string;
};

type UseCandidate = Omit<LimitedUse, 'max' | 'spent'> & {max?: FormulaValue};

export type SpellcastingSummary = SpellcastingSource & {
	saveDifficulty?: number;
	attackBonus?: number;
};

const UNARMORED_ARMOR_CLASS = 10;
const EXHAUSTION_SPEED_PENALTY = 5;
const EXHAUSTION_D20_PENALTY = 2;

export const hitPointRollKey = (classId: string, classLevel: number) => `${classId}:${classLevel}`;

export const averageHitDie = (hitDie: number) => hitDie / 2 + 1;

const matchesRoll = (effect: {kind: string; ability?: string; skill?: string}, context: RollContext) =>
	effect.kind === (context.kind === 'initiative' ? 'check' : context.kind) &&
	(!effect.ability || effect.ability === context.ability) &&
	(!effect.skill || effect.skill === context.skill);
const MEDIUM_ARMOR_DEXTERITY_CAP = 2;
export const ATTUNEMENT_SLOTS = 3;

export class Character {
	readonly name: string;
	readonly level: number;
	proficiencyBonus: number;
	readonly classes: CharacterClass[];
	readonly features: FeatureInstance[];
	readonly choices: ChoiceInstance[];

	readonly spentHitPoints: number;
	readonly temporaryHitPoints: number;
	readonly spentTemporaryHitPoints: number;

	private readonly catalog: Catalog;
	private readonly state: CharacterState;
	private readonly scope: ChoiceScope;
	private readonly propsSources: PropsSource[];
	private readonly abilityScores: Record<Ability, number>;
	/** выбранные повышения, которые упираются в предел характеристики */
	readonly abilityOverflows: AbilityOverflow[];
	private readonly walkingSpeed: number;
	private readonly effects: CharacterEffects;
	readonly equipment: EquippedItem[];
	/** выборы предметов и действующих заклинаний: ключ владельца → ключ группы → значения */
	private readonly externalChoices: Map<string, Record<string, string[]>>;

	constructor(
		state: CharacterState,
		characterFeatures: CharacterFeatures,
		catalog: Catalog,
		itemRecords: Map<string, ItemRecord> = new Map(),
	) {
		this.equipment = buildEquipment(state, itemRecords);
		this.state = state;
		this.catalog = catalog;
		this.name = state.name;
		this.level = characterLevel(state.classes);
		this.proficiencyBonus = proficiencyBonusForLevel(this.level);
		this.externalChoices = new Map([
			...itemChoiceValues(this.equipment),
			...activeSpellChoiceValues(state.activeSpells || []),
		]);
		this.classes = characterFeatures.classes.map(({classData, classLevel, subClass, isStartingClass}) => ({
			classData,
			level: classLevel.level,
			subClass,
			isStartingClass,
		}));

		this.spentHitPoints = state.hitPoints.spent;
		this.temporaryHitPoints = state.hitPoints.temporary;
		this.spentTemporaryHitPoints = state.hitPoints.temporarySpent;

		this.features = allFeatureInstances(characterFeatures);
		this.choices = this.features.flatMap((instance) => flattenChoices(instance.choices));
		this.scope = new ChoiceScope(this.features);
		this.propsSources = [
			...collectPropsSources(this.features),
			...itemPropsSources(this.equipment, this.level),
			...activeSpellPropsSources(state.activeSpells || [], catalog, this.level),
		];
		this.abilityScores = withAbilityMinimums(
			calculateAbilityScores(state.baseAbilities, this.propsSources, this.choices),
			this.propsSources,
		);
		this.abilityOverflows = abilityOverflows(state.baseAbilities, this.propsSources, this.choices);
		this.walkingSpeed = this.calculateWalkingSpeed();
		this.effects = collectEffects(
			this.features,
			this.propsSources,
			(owner, scopes) => this.formulaContext(owner, scopes),
			state.spellbookSpells,
		);
		this.proficiencyBonus += this.effects.proficiencyBonus;
	}

	formulaContext(owner: FeatureOwner, scopes: string[]): FormulaContext {
		const tables = owner.classId ? this.classTables(owner.classId) : [];
		return {
			level: owner.level,
			proficiencyBonus: this.proficiencyBonus,
			speed: this.walkingSpeed,
			abilityModifier: (ability) => this.abilityModifier(ability),
			tableValue: (column) => {
				const table = tables.find((candidate) => candidate.columns.includes(column));
				return table?.rows[owner.level - 1]?.[table.columns.indexOf(column)] ?? undefined;
			},
			variable: (name) =>
				this.scope.values(name, scopes)?.[0] ?? this.externalChoices.get(owner.key)?.[name]?.[0],
		};
	}

	private classTables(classId: string): SpellTable[] {
		const subClassTables = this.features
			.filter((instance) => instance.owner.classId === classId)
			.flatMap((instance) => instance.feature.props?.spellcasting?.table || []);
		const classTable = this.catalog.getClass(classId)?.table;
		return classTable ? [classTable, ...subClassTables] : subClassTables;
	}

	choiceValues(name: string, scopes: string[]): string[] {
		return this.scope.values(name, scopes) || [];
	}

	abilityScore(ability: Ability): number {
		return this.abilityScores[ability];
	}

	abilityModifier(ability: Ability): number {
		return abilityModifier(this.abilityScore(ability));
	}

	isSavingThrowProficient(ability: Ability): boolean {
		return this.effects.savingThrows.has(ability);
	}

	savingThrowBonus(ability: Ability): number {
		const proficiency = this.isSavingThrowProficient(ability) ? this.proficiencyBonus : 0;
		return this.abilityModifier(ability) + proficiency + this.effects.savingThrowBonus;
	}

	abilityCheckBonus(ability: Ability): number {
		return this.abilityModifier(ability) + this.effects.abilityCheckBonus;
	}

	rollBonuses(context: RollContext): Sourced<RollBonus>[] {
		return this.effects.rollBonuses.filter((bonus) => matchesRoll(bonus, context));
	}

	get unarmedStrikeBonus() {
		return this.effects.unarmedStrike;
	}

	get savingThrowAdvantages(): string[] {
		return [...this.effects.savingThrowAdvantages];
	}

	get conditionAdvantages(): string[] {
		return [...this.effects.conditionAdvantages];
	}

	skillProficiency(skill: string): ProficiencyLevel {
		if (this.effects.expertise.has(skill)) return 'expertise';
		if (this.effects.skills.has(skill)) return 'proficient';
		return 'none';
	}

	skillBonus(skill: string): number {
		const multiplier = {none: 0, proficient: 1, expertise: 2}[this.skillProficiency(skill)];
		return this.abilityCheckBonus(SKILL_ABILITY[skill]) + multiplier * this.proficiencyBonus;
	}

	get passivePerception(): number {
		return 10 + this.skillBonus('Perception');
	}

	get initiative(): number {
		return this.abilityCheckBonus(Ability.Dexterity);
	}

	get armorProficiencies(): string[] {
		return [...this.effects.armor];
	}

	get weaponProficiencies(): string[] {
		return [...this.effects.weapons];
	}

	get toolProficiencies(): string[] {
		return [...this.effects.tools];
	}

	get instrumentProficiencies(): string[] {
		return [...this.effects.instruments];
	}

	get weaponMasteries(): string[] {
		return [...this.effects.weaponMasteries];
	}

	get languages(): string[] {
		return [...this.effects.languages];
	}

	get senses(): Partial<Record<Sense, number>> {
		return this.effects.senses;
	}

	get calculatedSpeeds(): Speeds {
		const penalty = EXHAUSTION_SPEED_PENALTY * this.exhaustion;
		const multiplier = this.effects.speedMultiplier;
		const speeds: Speeds = {walk: this.walkingSpeed, ...this.effects.extraSpeeds};
		return Object.fromEntries(
			Object.entries(speeds).map(([kind, speed]) => [kind, Math.max(0, (speed || 0) * multiplier - penalty)]),
		) as Speeds;
	}

	get speeds(): Speeds {
		const overrides = Object.fromEntries(
			Object.entries(this.state.overrides.speeds || {}).filter(([, speed]) => speed != null),
		);
		const speeds: Speeds = {...this.calculatedSpeeds, ...overrides};
		if (!hasZeroSpeed(this.state.conditions)) return speeds;
		return Object.fromEntries(Object.keys(speeds).map((kind) => [kind, 0])) as Speeds;
	}

	rollAdvice(context: RollContext): RollAdvice {
		const modes = this.effects.rollModes.filter((mode) => matchesRoll(mode, context));
		return rollAdvice(this.state.conditions, context, modes);
	}

	get exhaustion(): number {
		return this.state.exhaustion || 0;
	}

	get d20Penalty(): number {
		return EXHAUSTION_D20_PENALTY * this.exhaustion;
	}

	get size(): string | undefined {
		const specie = this.state.specieId ? this.catalog.getSpecie(this.state.specieId) : undefined;
		if (!specie) return undefined;
		if (this.state.size && specie.sizes.includes(this.state.size)) return this.state.size;
		return specie.sizes.includes(Size.Medium) ? Size.Medium : specie.sizes[0];
	}

	/** особенности и предметы, которые дали защиту: устойчивость, невосприимчивость, преимущество */
	defenseSources(
		effect:
			| 'resistances'
			| 'immunities'
			| 'vulnerabilities'
			| 'conditionImmunities'
			| 'savingThrowAdvantages'
			| 'conditionAdvantages',
		value: string,
	): string[] {
		return this.effects.listSources.get(`${effect}:${value}`) || [];
	}

	get resistances(): string[] {
		return [...this.effects.resistances];
	}

	get immunities(): string[] {
		return [...this.effects.immunities];
	}

	get vulnerabilities(): string[] {
		return [...this.effects.vulnerabilities];
	}

	get conditionImmunities(): string[] {
		return [...this.effects.conditionImmunities];
	}

	get armorClass(): number {
		return this.armorClassParts.reduce((total, {value}) => total + value, 0);
	}

	/** слагаемые КБ: основа от доспеха или защиты без доспехов, щит, бонусы особенностей и предметов */
	get armorClassParts(): ArmorClassBonus[] {
		const shield = this.equippedItems.find(({item}) => isShield(item));
		const worn = this.equippedItems.find(({item}) => isBodyArmor(item));
		return [
			...this.baseArmorClassParts(worn, !!shield),
			...(shield ? [{title: shield.item.title, value: shield.item.armorClass || 0}] : []),
			...this.effects.armorClassBonuses,
			...(worn ? this.effects.armoredArmorClassBonuses : []),
		];
	}

	private baseArmorClassParts(worn: EquippedItem | undefined, hasShield: boolean): ArmorClassBonus[] {
		const dexterity = this.abilityModifier(Ability.Dexterity);
		const dexterityTitle = abilityLabel(Ability.Dexterity);
		if (worn) {
			const armor = worn.item;
			const armorDexterity = {
				Light: dexterity,
				Medium: Math.min(dexterity, MEDIUM_ARMOR_DEXTERITY_CAP),
				Heavy: 0,
				Shield: 0,
			}[armor.armorKind!];
			const title = armor.baseTitle ? `${armor.title} (${armor.baseTitle})` : armor.title;
			return [
				{title, value: armor.armorClass || UNARMORED_ARMOR_CLASS},
				...(armor.armorKind === 'Heavy'
					? []
					: [
							{
								title:
									armor.armorKind === 'Medium' ? `${dexterityTitle} (не больше +2)` : dexterityTitle,
								value: armorDexterity,
							},
					  ]),
			];
		}
		const options: ArmorClassBonus[][] = [
			[
				{title: 'Без доспехов', value: UNARMORED_ARMOR_CLASS},
				{title: dexterityTitle, value: dexterity},
			],
			...this.effects.unarmoredDefenses
				.filter(({withoutShield}) => !withoutShield || !hasShield)
				.map(({title, base, abilities}) => [
					{title, value: base},
					{title: dexterityTitle, value: dexterity},
					...abilities.map((ability) => ({
						title: abilityLabel(ability),
						value: this.abilityModifier(ability),
					})),
				]),
		];
		const sum = (parts: ArmorClassBonus[]) => parts.reduce((total, {value}) => total + value, 0);
		return options.reduce((best, option) => (sum(option) > sum(best) ? option : best));
	}

	get equippedItems(): EquippedItem[] {
		return this.equipment.filter(({entry}) => entry.equipped);
	}

	isAttuned(entryKey: string): boolean {
		return this.state.attunement.includes(entryKey);
	}

	/** оружие в руках: стопка одинакового оружия даёт одну атаку, боеприпасы не атакуют */
	get heldWeapons(): EquippedItem[] {
		return stackEquipment(this.equippedItems)
			.map(({entries}) => entries[0])
			.filter(({item}) => isWeapon(item) && !isAmmunition(item));
	}

	get weaponAttacks(): Attack[] {
		return this.heldWeapons.filter(({entry}) => !entry.offHand).map((equipped) => weaponAttack(this, equipped));
	}

	get offHandAttacks(): Attack[] {
		return this.heldWeapons
			.filter(({entry}) => entry.offHand)
			.map((equipped) => weaponAttack(this, equipped, true));
	}

	get unarmedStrike(): Attack {
		return unarmedStrike(this);
	}

	/** Внеочередная атака — одна атака оружием ближнего боя или Безоружный удар */
	get opportunityAttacks(): Attack[] {
		const weapons = this.heldWeapons
			.filter(({entry, item}) => !entry.offHand && !isRangedWeapon(item))
			.map((equipped) => weaponAttack(this, equipped));
		return [...weapons, this.unarmedStrike];
	}

	get unarmedDexterity(): boolean {
		return this.effects.unarmedDexterity;
	}

	get unarmedDamageDie(): string | undefined {
		const dice = this.effects.notes
			.filter((note) => note.kind === 'unarmedDamageDie' && note.value != null)
			.map((note) => String(note.value));
		return dice.sort((first, second) => dieSides(second) - dieSides(first))[0];
	}

	get carriedWeight(): number {
		return equipmentWeight(this.equipment, this.state.coins);
	}

	get carryingCapacity(): CarryingCapacity {
		return carryingCapacity(
			(this.size as Size) || Size.Medium,
			this.abilityScore(Ability.Strength),
			this.effects.carryingSizeBonus,
		);
	}

	get attacksPerAction(): number {
		return 1 + this.effects.extraAttacks;
	}

	get hitDice(): HitDice[] {
		const countByDie = new Map<number, number>();
		for (const {classData, level} of this.classes) {
			countByDie.set(classData.hitDie, (countByDie.get(classData.hitDie) || 0) + level);
		}
		return [...countByDie].map(([die, count]) => ({
			die,
			count,
			spent: Math.min(count, this.state.spentHitDice[die] || 0),
		}));
	}

	get calculatedMaxHitPoints(): number {
		const constitution = this.abilityModifier(Ability.Constitution);
		const classHitPoints = this.classes.reduce(
			(total, {classData, level}) =>
				total +
				Array.from({length: level}, (_, index) => this.hitDieValue(classData, index + 1)).reduce(
					(sum, value) => sum + value,
					0,
				),
			0,
		);
		const bonus = this.effects.hitPointsBonus + (this.effects.hitPointsPerLevel + constitution) * this.level;
		return Math.max(this.level, classHitPoints + bonus);
	}

	hitDieValue(classData: ClassData, classLevel: number): number {
		const isStartingClass = this.classes[0]?.classData.id === classData.id;
		if (isStartingClass && classLevel === 1) return classData.hitDie;
		return this.state.hitPointRolls[hitPointRollKey(classData.id, classLevel)] ?? averageHitDie(classData.hitDie);
	}

	get maxHitPoints(): number {
		const {maxHitPoints, maxHitPointsBonus} = this.state.overrides;
		if (maxHitPoints != null) return Math.max(1, maxHitPoints);
		return this.calculatedMaxHitPoints + Math.max(0, maxHitPointsBonus || 0);
	}

	get currentHitPoints(): number {
		return Math.max(0, this.maxHitPoints - this.spentHitPoints);
	}

	get currentTemporaryHitPoints(): number {
		return Math.max(0, this.temporaryHitPoints - this.spentTemporaryHitPoints);
	}

	takeDamage(damage: number): HitPointsState {
		const absorbed = Math.min(damage, this.currentTemporaryHitPoints);
		return {
			temporary: this.temporaryHitPoints,
			temporarySpent: this.spentTemporaryHitPoints + absorbed,
			spent: Math.min(this.maxHitPoints, this.spentHitPoints + damage - absorbed),
		};
	}

	heal(amount: number): HitPointsState {
		return {
			temporary: this.temporaryHitPoints,
			temporarySpent: this.spentTemporaryHitPoints,
			spent: Math.max(0, this.spentHitPoints - amount),
		};
	}

	receiveTemporaryHitPoints(amount: number): HitPointsState {
		const keepCurrent = this.currentTemporaryHitPoints >= amount;
		return {
			spent: this.spentHitPoints,
			temporary: keepCurrent ? this.temporaryHitPoints : amount,
			temporarySpent: keepCurrent ? this.spentTemporaryHitPoints : 0,
		};
	}

	get canCastSpells(): boolean {
		return this.effects.spellcasting.length > 0 || this.effects.spells.length > 0;
	}

	get spellcasting(): SpellcastingSummary[] {
		return this.effects.spellcasting.map((source) =>
			source.ability ? {...source, ...this.spellStats(source.ability)} : source,
		);
	}

	spellStats(ability: Ability): {saveDifficulty: number; attackBonus: number; modifier: number} {
		const modifier = this.abilityModifier(ability);
		return {
			saveDifficulty: 8 + this.proficiencyBonus + modifier + this.effects.spellSaveDcBonus,
			attackBonus: this.proficiencyBonus + modifier + this.effects.spellAttackBonus,
			modifier,
		};
	}

	/** заклинания, вписанные в книгу класса на листе */
	spellbookAdditions(classId: string): string[] {
		return this.state.spellbookSpells?.[classId] || [];
	}

	get knownSpells(): CharacterSpell[] {
		return this.effects.spells;
	}

	get preparedSpells(): CharacterSpell[] {
		return this.effects.spells.filter(
			(spell) =>
				!spell.needsPreparation ||
				spell.prepared ||
				spell.alwaysPrepared ||
				this.catalog.getSpell(spell.id)?.level === 0,
		);
	}

	get spellSlots(): SpellSlot[] {
		return calculateSpellSlots(
			this.classes,
			this.effects.spellcasting,
			findFullCasterTable(this.catalog.classes),
			this.state.spentSpellSlots,
		);
	}

	get resources(): CharacterResource[] {
		return this.effects.resources;
	}

	get actions(): CharacterAction[] {
		return this.effects.actions;
	}

	get combatNotes(): CombatNote[] {
		return this.effects.notes;
	}

	get limitedUses(): LimitedUse[] {
		const resourceUses: UseCandidate[] = this.effects.resources.map((resource) => ({
			key: resource.key,
			title: resource.title,
			max: resource.max,
			recharge: resource.recharge,
			regainsOnShortRest: resource.regainsOnShortRest,
			regain: resource.regain,
		}));
		const actionUses: UseCandidate[] = this.effects.actions
			.filter((action) => !action.resource)
			.map((action) => ({
				key: action.key,
				title: action.title,
				max: action.usage,
				recharge: action.recharge,
				regainsOnShortRest: action.regainsOnShortRest,
			}));
		const spellUses: UseCandidate[] = this.effects.spells.map((spell) => ({
			key: `spell:${spell.id}`,
			title: this.catalog.getSpell(spell.id)?.title || spell.id,
			max: spell.usage,
			recharge: spell.recharge,
		}));
		return [...resourceUses, ...actionUses, ...spellUses].flatMap(({max, ...use}) =>
			typeof max === 'number' && max > 0
				? [{...use, max, spent: Math.min(max, this.state.spentUses[use.key] || 0)}]
				: [],
		);
	}

	limitedUse(key: string): LimitedUse | undefined {
		return this.limitedUses.find((use) => use.key === key);
	}

	maxSpellLevel(classId: string): number {
		const characterClass = this.classes.find(({classData}) => classData.id === classId);
		if (!characterClass) return 0;
		return maxSpellLevel(characterClass, this.effects.spellcasting, findFullCasterTable(this.catalog.classes));
	}

	getFeatureInstance(key: string): FeatureInstance | undefined {
		return this.features.find((instance) => instance.key === key);
	}

	private calculateWalkingSpeed(): number {
		const specie = this.state.specieId ? this.catalog.getSpecie(this.state.specieId) : undefined;
		const baseSpeeds = this.propsSources.map(({props}) => props.speed).filter((speed): speed is number => !!speed);
		const baseSpeed = baseSpeeds.length ? Math.max(...baseSpeeds) : specie?.speed || 0;
		const bonus = this.propsSources.reduce((total, {props}) => total + (Number(props.speedBonus) || 0), 0);
		return baseSpeed + bonus;
	}
}

const dieSides = (die: string) => Number(die.match(/d(\d+)/i)?.[1]) || 0;
